import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LoginOrganization } from '@quire/shared';
import { ApiError, api } from '../lib/api';
import { OrganizationChoices } from '../components/OrganizationChoices';
import { ErrorNote, Frame } from '../components/ui';

function message(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Could not reach the server. Check your connection and try again.';
}

/**
 * Where to land after signing in.
 *
 * A platform administrator belongs to no organization until they enter one, so
 * sending them to the register would show an empty screen and a refused
 * request. They start by choosing where to work.
 */
function useSignedIn() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    const me = await queryClient.fetchQuery({ queryKey: ['me'], queryFn: api.me });
    const destination = me.role === 'super_admin' && !me.organization ? '/enter' : '/classes';
    await navigate({ to: destination });
  };
}

/**
 * Sign in with an address and a password; the organization comes from the
 * account. `orgSlug` is only set when arriving from an organization's own
 * sign-in link, and narrows the lookup to it.
 */
function SignInForm({ orgSlug }: { orgSlug?: string }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [choices, setChoices] = useState<LoginOrganization[] | null>(null);
  const onSignedIn = useSignedIn();

  const signIn = useMutation({
    mutationFn: (organizationId?: string | null) =>
      api.login({
        identifier,
        password,
        ...(orgSlug ? { orgSlug } : {}),
        ...(organizationId === undefined ? {} : { organizationId }),
      }),
    onSuccess: async (result) => {
      if ('needsOrganization' in result) {
        setChoices(result.organizations);
        return;
      }
      await onSignedIn();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setChoices(null);
    signIn.mutate(undefined);
  };

  if (choices) {
    return (
      <div className="grid gap-4">
        <p className="text-sm text-ink-soft">
          This account exists at more than one organization. Choose the one to open.
        </p>
        <OrganizationChoices
          choices={choices}
          disabled={signIn.isPending}
          onChoose={(id) => signIn.mutate(id)}
        />
        <button
          type="button"
          className="text-sm text-ink-soft underline"
          onClick={() => setChoices(null)}
        >
          Use a different account
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid gap-4">
      {signIn.error ? <ErrorNote message={message(signIn.error)} /> : null}
      <label className="grid gap-1.5">
        <span className="eyebrow">Email or username</span>
        <input
          className="field"
          // Not type="email": a platform account signs in with a username, and
          // the browser would refuse to submit one.
          type="text"
          autoComplete="username"
          autoFocus
          required
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
        />
      </label>
      <label className="grid gap-1.5">
        <span className="eyebrow">Password</span>
        <input
          className="field"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </label>
      <button className="btn btn-primary mt-1" type="submit" disabled={signIn.isPending}>
        {signIn.isPending ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}

/** The ordinary entry point. Nothing to know but your own address. */
export function LoginPicker() {
  return (
    <Frame title="Sign in">
      <SignInForm />
    </Frame>
  );
}

/** An organization's own sign-in link, which pins the lookup to that org. */
export function OrgLogin() {
  const { slug } = useParams({ from: '/o/$slug/login' });
  return (
    <Frame title="Sign in" subtitle={`Organization: ${slug}`}>
      <SignInForm orgSlug={slug} />
      <p className="mt-4 text-center text-sm">
        <Link to="/login" className="text-ink-soft underline">
          Sign in without naming an organization
        </Link>
      </p>
    </Frame>
  );
}

export function AdminLogin() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const onSignedIn = useSignedIn();

  const signIn = useMutation({
    mutationFn: () => api.adminLogin(identifier, password),
    onSuccess: onSignedIn,
  });

  return (
    <Frame title="Platform sign in" subtitle="For administrators of every organization">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          signIn.mutate();
        }}
        className="grid gap-4"
      >
        {signIn.error ? <ErrorNote message={message(signIn.error)} /> : null}
        <label className="grid gap-1.5">
          <span className="eyebrow">Email or username</span>
          <input
            className="field"
            type="text"
            autoComplete="username"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </label>
        <label className="grid gap-1.5">
          <span className="eyebrow">Password</span>
          <input
            className="field"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button className="btn btn-primary mt-1" type="submit" disabled={signIn.isPending}>
          {signIn.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </Frame>
  );
}
